import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { type ActionFunctionArgs, json, type MetaFunction } from '@remix-run/node'
import { Link, useFetcher } from '@remix-run/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { ErrorBulletList, Field } from '#app/components/forms/forms.tsx'
import { StatusButton } from '#app/components/forms/status-button.tsx'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { EmailSchema } from '#app/utils/user-validation.ts'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { postAndReturnStatus } from '#app/utils/refundr.server.aragorn.ts'
import { redirectWithToast } from '#app/utils/toast.server.ts'

export const meta: MetaFunction = () => {
	return [{ title: 'Forgot Password | refundr' }]
}

const ForgotPasswordSchema = z.object({
	email: EmailSchema,
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()

	// Honeypot is a simple technique to prevent spam bots from submitting forms. It works by adding a hidden field to the form that bots will fill, but humans won't.
	checkHoneypot(formData)

	const submission = await parseWithZod(formData, {
		schema: ForgotPasswordSchema.superRefine(async (data, ctx) => {
			const response = await postAndReturnStatus<{ email: string }>(
				request,
				'/aws/forgot-password/',
				{
					email: data.email,
				},
				true,
			)
			if (!response?.success && 400 === response?.statusCode) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: response.data,
				})
				return z.NEVER
			}
			return { ...data, response: response.data }
		}),
		async: true,
	})

	if (submission.status !== 'success') {
		return json({ result: submission.reply({ hideFields: ['password'] }) }, { status: submission.status === 'error' ? 400 : 200 })
	}

	return redirectWithToast('/reset-password', {
		description: 'We have sent you a verification code by email and instructions to reset your password.',
		type: 'success',
		title: 'Success',
	})
}

export default function ForgotPasswordRoute() {
	const forgotPassword = useFetcher<typeof action>()

	const [form, fields] = useForm({
		id: 'forgot-password-form',
		constraint: getZodConstraint(ForgotPasswordSchema),
		lastResult: forgotPassword.data?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: ForgotPasswordSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="flex min-h-screen flex-col md:flex-row">
			{/* Left: Marketing Section */}
			<div className="dark-background order-last flex w-full items-center justify-center p-6 text-black md:order-none md:w-1/2">
				<div className="mt-5 flex flex-col items-center justify-center space-y-4">
					<div>
						<img className="block h-12 w-auto" src="/img/logo/refundr-white.png" alt="refundr" />
					</div>
					<div className="text-center font-mono text-gray-200">Re-inventing how you connect with your Tax Clients.</div>
					<div className="pt-4">
						<img className="block w-auto" src="/img/tax-return-card-md.png" alt="refundr" />
					</div>
				</div>
			</div>

			{/* Right: Login Section */}
			<div className="flex w-full items-center justify-center bg-white p-6 md:w-1/2">
				<div className="mb-16 mt-5 w-full max-w-md bg-white">
					<div className="mb-6 flex items-center justify-center">
						<div className="text-2xl font-bold text-gray-600">Forgot Password</div>
					</div>
					<div className="mb-6 flex items-center justify-center">
						<div className="mt-3 text-body-sm text-muted-foreground">No worries, we'll help you get a new one setup.</div>
					</div>
					<div>
						<forgotPassword.Form method="POST" {...getFormProps(form)}>
							<ErrorBulletList errors={form.errors} id={form.errorId} />

							<HoneypotInputs />
							<div>
								<Field
									labelProps={{
										htmlFor: fields.email.id,
										children: 'Email',
										className: 'text-gray-700',
									}}
									inputProps={{
										autoFocus: true,
										...getInputProps(fields.email, { type: 'email' }),
										className: 'lowercase h-14 w-full rounded-lg bg-gray-100 px-3 py-2 text-black shadow border-gray-300',
										placeholder: 'enter your email',
									}}
									errors={fields.email.errors}
								/>
							</div>
							<div>
								<StatusButton
									className="h-12 w-full rounded rounded-md bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
									status={forgotPassword.state === 'submitting' ? 'pending' : form.status ?? 'idle'}
									type="submit"
									disabled={forgotPassword.state !== 'idle'}
								>
									Send verification code by email
								</StatusButton>
							</div>
							<div className="mt-5">
								<Link to="/reset-password" className="text-right text-body-sm font-bold text-gray-500">
									Reset password page
								</Link>
							</div>
						</forgotPassword.Form>
					</div>
				</div>
			</div>
		</div>
	)
}
